import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
import { dbOperation } from '../utils/database';

export interface ServiceStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'checking';
  responseTime?: number;
  lastChecked?: Date;
  error?: string;
  details?: any;
}

export interface HealthCheckResult {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: Date;
  services: ServiceStatus[];
  issues: string[];
}

/**
 * Service Health Check Manager
 * Monitors the health of all critical services and integrations
 */
export class HealthCheckService {
  private static isChecking = false;
  private static lastCheck: HealthCheckResult | null = null;
  private static checkInterval: NodeJS.Timer | null = null;

  /**
   * Perform a comprehensive health check
   */
  static async checkHealth(): Promise<HealthCheckResult> {
    if (this.isChecking) {
      logger.warn('Health check already in progress');
      return this.lastCheck || this.getDefaultResult();
    }

    this.isChecking = true;
    const services: ServiceStatus[] = [];
    const issues: string[] = [];
    const startTime = Date.now();

    try {
      // 1. Check Supabase Database
      services.push(await this.checkDatabase());

      // 2. Check Authentication Service
      services.push(await this.checkAuth());

      // 3. Check Edge Functions
      services.push(await this.checkEdgeFunctions());

      // 4. Check Storage
      services.push(await this.checkStorage());

      // 5. Check Real-time Subscriptions
      services.push(await this.checkRealtime());

      // 6. Check External APIs (GitHub, Jira, etc.)
      services.push(await this.checkExternalAPIs());

      // Analyze results
      const unhealthyServices = services.filter(s => s.status === 'unhealthy');
      const degradedServices = services.filter(s => s.status === 'degraded');

      unhealthyServices.forEach(s => {
        issues.push(`${s.name} is unhealthy: ${s.error || 'Unknown error'}`);
      });

      degradedServices.forEach(s => {
        issues.push(`${s.name} is degraded: ${s.error || 'Performance issues'}`);
      });

      // Determine overall status
      let overall: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
      if (unhealthyServices.length > 0) {
        overall = 'unhealthy';
      } else if (degradedServices.length > 0) {
        overall = 'degraded';
      }

      const result: HealthCheckResult = {
        overall,
        timestamp: new Date(),
        services,
        issues
      };

      this.lastCheck = result;
      
      // Log health check result
      logger.info('Health check completed', {
        overall,
        duration: Date.now() - startTime,
        unhealthy: unhealthyServices.length,
        degraded: degradedServices.length
      });

      // Store health check result in database for monitoring
      await this.storeHealthCheckResult(result);

      return result;
    } catch (error) {
      logger.error('Health check failed', { error });
      return this.getDefaultResult();
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Check database connectivity and performance
   */
  private static async checkDatabase(): Promise<ServiceStatus> {
    const startTime = Date.now();
    try {
      const result = await dbOperation(
        () => supabase
          .from('users')
          .select('count')
          .limit(1),
        {
          operation: 'health check',
          entity: 'database',
          showUserError: false
        }
      );

      const responseTime = Date.now() - startTime;

      if (!result.success) {
        return {
          name: 'Database',
          status: 'unhealthy',
          responseTime,
          lastChecked: new Date(),
          error: result.error
        };
      }

      // Check response time
      if (responseTime > 2000) {
        return {
          name: 'Database',
          status: 'degraded',
          responseTime,
          lastChecked: new Date(),
          error: 'Slow response time'
        };
      }

      return {
        name: 'Database',
        status: 'healthy',
        responseTime,
        lastChecked: new Date()
      };
    } catch (error) {
      return {
        name: 'Database',
        status: 'unhealthy',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check authentication service
   */
  private static async checkAuth(): Promise<ServiceStatus> {
    const startTime = Date.now();
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      const responseTime = Date.now() - startTime;

      if (error) {
        return {
          name: 'Authentication',
          status: 'unhealthy',
          responseTime,
          lastChecked: new Date(),
          error: error.message
        };
      }

      return {
        name: 'Authentication',
        status: 'healthy',
        responseTime,
        lastChecked: new Date(),
        details: { hasSession: !!session }
      };
    } catch (error) {
      return {
        name: 'Authentication',
        status: 'unhealthy',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check edge functions
   */
  private static async checkEdgeFunctions(): Promise<ServiceStatus> {
    const startTime = Date.now();
    try {
      // Call health check edge function
      const { data, error } = await supabase.functions.invoke('health-check', {
        body: { test: true }
      });

      const responseTime = Date.now() - startTime;

      if (error) {
        return {
          name: 'Edge Functions',
          status: 'unhealthy',
          responseTime,
          lastChecked: new Date(),
          error: error.message
        };
      }

      if (responseTime > 3000) {
        return {
          name: 'Edge Functions',
          status: 'degraded',
          responseTime,
          lastChecked: new Date(),
          error: 'Slow response time'
        };
      }

      return {
        name: 'Edge Functions',
        status: 'healthy',
        responseTime,
        lastChecked: new Date(),
        details: data
      };
    } catch (error) {
      // Edge functions might not be deployed yet
      return {
        name: 'Edge Functions',
        status: 'degraded',
        lastChecked: new Date(),
        error: 'Edge functions not available'
      };
    }
  }

  /**
   * Check storage service
   */
  private static async checkStorage(): Promise<ServiceStatus> {
    const startTime = Date.now();
    try {
      const { data, error } = await supabase.storage
        .from('attachments')
        .list('', { limit: 1 });

      const responseTime = Date.now() - startTime;

      if (error) {
        return {
          name: 'Storage',
          status: 'unhealthy',
          responseTime,
          lastChecked: new Date(),
          error: error.message
        };
      }

      return {
        name: 'Storage',
        status: 'healthy',
        responseTime,
        lastChecked: new Date()
      };
    } catch (error) {
      return {
        name: 'Storage',
        status: 'degraded',
        lastChecked: new Date(),
        error: 'Storage service not configured'
      };
    }
  }

  /**
   * Check real-time subscriptions
   */
  private static async checkRealtime(): Promise<ServiceStatus> {
    try {
      return new Promise((resolve) => {
        const startTime = Date.now();
        const timeout = setTimeout(() => {
          resolve({
            name: 'Realtime',
            status: 'unhealthy',
            lastChecked: new Date(),
            error: 'Connection timeout'
          });
        }, 5000);

        const channel = supabase.channel('health-check')
          .on('presence', { event: 'sync' }, () => {
            clearTimeout(timeout);
            const responseTime = Date.now() - startTime;
            
            channel.unsubscribe();
            
            resolve({
              name: 'Realtime',
              status: 'healthy',
              responseTime,
              lastChecked: new Date()
            });
          })
          .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
              clearTimeout(timeout);
              channel.unsubscribe();
              
              resolve({
                name: 'Realtime',
                status: 'unhealthy',
                lastChecked: new Date(),
                error: 'Channel error'
              });
            }
          });
      });
    } catch (error) {
      return {
        name: 'Realtime',
        status: 'unhealthy',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check external API integrations
   */
  private static async checkExternalAPIs(): Promise<ServiceStatus> {
    const results = {
      github: false,
      jira: false,
      openai: false,
      anthropic: false
    };

    try {
      // Check if user has API connections configured
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Check for connected services
        const { data: connections } = await supabase
          .from('user_connections')
          .select('provider, status')
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (connections) {
          connections.forEach(conn => {
            if (conn.provider in results) {
              results[conn.provider as keyof typeof results] = true;
            }
          });
        }

        // Check for API keys
        const { data: apiKeys } = await supabase
          .from('user_api_keys')
          .select('provider')
          .eq('user_id', user.id);

        if (apiKeys) {
          apiKeys.forEach(key => {
            if (key.provider in results) {
              results[key.provider as keyof typeof results] = true;
            }
          });
        }
      }

      const connectedCount = Object.values(results).filter(v => v).length;
      const totalCount = Object.keys(results).length;

      if (connectedCount === 0) {
        return {
          name: 'External APIs',
          status: 'degraded',
          lastChecked: new Date(),
          error: 'No external APIs connected',
          details: results
        };
      }

      return {
        name: 'External APIs',
        status: connectedCount === totalCount ? 'healthy' : 'degraded',
        lastChecked: new Date(),
        details: results
      };
    } catch (error) {
      return {
        name: 'External APIs',
        status: 'degraded',
        lastChecked: new Date(),
        error: 'Unable to check API connections'
      };
    }
  }

  /**
   * Store health check result for monitoring
   */
  private static async storeHealthCheckResult(result: HealthCheckResult): Promise<void> {
    try {
      await supabase
        .from('system_health_checks')
        .insert({
          status: result.overall,
          services: result.services,
          issues: result.issues,
          timestamp: result.timestamp
        });
    } catch (error) {
      // Don't fail health check if we can't store the result
      logger.warn('Failed to store health check result', { error });
    }
  }

  /**
   * Get default result when health check fails
   */
  private static getDefaultResult(): HealthCheckResult {
    return {
      overall: 'unhealthy',
      timestamp: new Date(),
      services: [],
      issues: ['Health check failed to complete']
    };
  }

  /**
   * Start periodic health checks
   */
  static startPeriodicChecks(intervalMs: number = 60000): void {
    this.stopPeriodicChecks();
    
    // Initial check
    this.checkHealth();
    
    // Schedule periodic checks
    this.checkInterval = setInterval(() => {
      this.checkHealth();
    }, intervalMs);
    
    logger.info('Started periodic health checks', { intervalMs });
  }

  /**
   * Stop periodic health checks
   */
  static stopPeriodicChecks(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      logger.info('Stopped periodic health checks');
    }
  }

  /**
   * Get last health check result
   */
  static getLastResult(): HealthCheckResult | null {
    return this.lastCheck;
  }

  /**
   * Check if services are healthy enough to proceed
   */
  static async canProceed(requiredServices: string[] = []): Promise<{ 
    canProceed: boolean; 
    reason?: string 
  }> {
    const result = await this.checkHealth();
    
    if (result.overall === 'unhealthy') {
      return {
        canProceed: false,
        reason: 'System is currently unavailable. Please try again later.'
      };
    }
    
    // Check specific required services
    for (const serviceName of requiredServices) {
      const service = result.services.find(s => s.name === serviceName);
      if (!service || service.status === 'unhealthy') {
        return {
          canProceed: false,
          reason: `${serviceName} is currently unavailable.`
        };
      }
    }
    
    return { canProceed: true };
  }
}
